<template>
  <HeaderBar v-if="!isExternalDriver" />

  <ButtonBackContainer v-if="!isLastStep && !isPaused">
    <ButtonBack v-if="canGoBack" :to="backRoute">{{ t('back') }}</ButtonBack>
    <div v-if="!isExternalDriver" class="flex overflow-hidden pl-3">
      <div class="overflow-hidden">
        <HandoverCounterSelector
          v-if="stationCounters.length > 0 && session"
          :key="counterSelectKey"
          :counters="stationCounters"
          :model-value="session.counterId"
          @update:model-value="handleCounterChange"
        />
      </div>
      <CVButton outline variant="error" @click="onCancelClicked">
        <span class="hidden md:inline">{{ t('cancelHandover') }}</span>
        <span class="md:hidden">{{ t('cancel') }}</span>
        <XMarkIcon class="h-5 w-5" />
      </CVButton>
    </div>
  </ButtonBackContainer>

  <section class="carvia-container flex flex-col justify-center">
    <div class="flex justify-center space-y-3">
      <HandoverPausedNotice v-if="isPaused" :resume="resume" />
      <router-view v-else />
    </div>
  </section>
</template>

<script setup lang="ts">
import { ProcessStep } from '@carvia/ros-client-types';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useI18n } from 'vue-i18n';
import { useBooking } from '@/queries/use-bookings';
import { useUiStore } from '@/stores/ui.store';
import { computed, ref, watch, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isAxiosError } from 'axios';
import { useExternalDriver } from '@/hooks/use-external-driver';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { Alert } from '@/services/alert.service';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import { getBookingProcessLanguage, useTemporaryLocale } from '@/hooks/use-temporary-locale';
import {
  useProcessSessionTracking,
  LAST_COUNTER_ID_KEY,
  type ProcessStepDefinition,
} from '@/hooks/use-process-step-tracking';
import { useGlobalSettings } from '@/queries/use-global-settings';
import { useSurveysConductedWithQuery } from '@/queries/use-surveys-conducted';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import { ProcessSessionType } from '@carvia/ros-client-types';
import { useStation } from '@/queries/use-stations';
import HandoverCounterSelector from '@/views/handover/components/HandoverCounterSelector.vue';
import HandoverPausedNotice from '@/views/handover/components/HandoverPausedNotice.vue';
import { useFrozenHandoverInspectionRequired } from '@/components/inspections/handover-inspection-required';

const uiStore = useUiStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { data: booking, error: bookingError } = useBooking(route.params.id as string);
const { isExternalDriver } = useExternalDriver();

// After completion an External driver may only finish photo uploads (ADR 0021).
watch([booking, bookingError], ([currentBooking, error]) => {
  const isCompleted =
    (currentBooking && currentBooking.status !== BookingStatus.CONFIRMED) ||
    (isAxiosError(error) && error.response?.status === 403);
  if (isExternalDriver.value && isCompleted && route.name !== 'handoverDone') {
    void router.replace({ name: 'handoverDone', params: { id: route.params.id } });
  }
});
const { data: globalSettings } = useGlobalSettings();

const { data: surveysConducted, isSuccess: isSurveysConductedSuccess } =
  useSurveysConductedWithQuery(
    computed(() => ({
      filter: [
        {
          bookingIds: [route.params.id as string],
          ...(globalSettings.value?.showHandoverSurvey
            ? { surveyIds: [globalSettings.value.showHandoverSurvey] }
            : {}),
        },
        ...(booking.value?.customer
          ? [
              {
                customerIds: [booking.value?.customer?.id],
                ...(globalSettings.value?.showHandoverSurvey
                  ? { surveyIds: [globalSettings.value.showHandoverSurvey] }
                  : {}),
              },
            ]
          : []),
      ],
    })),
    computed(() => !!(booking.value?.id && globalSettings.value)),
  );

const handoverAllocation = computed(() => booking.value?.allocations[0]);
const { required: handoverInspectionRequired, isReady: handoverInspectionReady } =
  useFrozenHandoverInspectionRequired(booking, handoverAllocation);

const stepsReady = computed(() => {
  if (!globalSettings.value) return false;
  if (!handoverInspectionReady.value) return false;
  if (globalSettings.value.showHandoverSurvey && !isSurveysConductedSuccess.value) return false;
  return true;
});

const steps = computed<ProcessStepDefinition[]>(() => {
  const result: ProcessStepDefinition[] = [
    { processStep: ProcessStep.HANDOVER_OVERVIEW, routeName: 'handoverDetail' },
  ];

  if (globalSettings.value?.showHandoverSurvey && !surveysConducted.value?.items.length) {
    result.push({
      processStep: ProcessStep.HANDOVER_SURVEY,
      routeName: 'handoverSurvey',
      routeParams: { surveyId: globalSettings.value.showHandoverSurvey },
    });
  }

  result.push(
    { processStep: ProcessStep.HANDOVER_CLIENT_DATA, routeName: 'handoverClientData' },
    { processStep: ProcessStep.HANDOVER_PAYMENT, routeName: 'handoverPayment' },
    { processStep: ProcessStep.HANDOVER_CAR_STATUS, routeName: 'handoverCarStatus' },
  );

  if (handoverInspectionRequired.value) {
    result.push({ processStep: ProcessStep.HANDOVER_INSPECTION, routeName: 'handoverInspection' });
  }

  result.push(
    { processStep: ProcessStep.HANDOVER_CONTRACT, routeName: 'handoverRentalContract' },
    { processStep: null, routeName: 'handoverDone' },
  );

  return result;
});

const pickupStationId = computed(() => booking.value?.pickupStation?.id ?? '');
const { data: pickupStationDetail } = useStation(
  pickupStationId,
  computed(() => !!pickupStationId.value),
);

const stationCounters = computed(
  () => pickupStationDetail.value?.counters ?? booking.value?.pickupStation?.counters ?? [],
);

function resolveCreatePayload() {
  const currentBooking = booking.value;
  if (!currentBooking) {
    return { type: ProcessSessionType.HANDOVER };
  }
  const isDeliveryHandover =
    isExternalDriver.value || currentBooking.pickupLocationType === BookingLocationType.STREET;
  if (isDeliveryHandover) {
    return { type: ProcessSessionType.HANDOVER, counterId: null };
  }
  const lastCounterId = localStorage.getItem(LAST_COUNTER_ID_KEY);
  const validCounterId =
    lastCounterId && stationCounters.value.some((c) => c.id === lastCounterId)
      ? lastCounterId
      : null;
  return { type: ProcessSessionType.HANDOVER, counterId: validCounterId };
}

const {
  clearProcessSession,
  backRoute,
  canGoBack,
  navigateToList,
  isLastStep,
  session,
  isPaused,
  resume,
  patchCounterId,
} = useProcessSessionTracking({
  booking,
  steps,
  listRouteName: 'upcomingHandovers',
  stepsReady,
  sessionType: ProcessSessionType.HANDOVER,
  resolveCreatePayload,
});

const counterSelectKey = ref(0);

const handleCounterChange = async (counterId: string | null) => {
  try {
    await patchCounterId(counterId);
  } catch {
    counterSelectKey.value += 1;
  }
};

const hasActiveCounter = computed(() =>
  session.value ? !!session.value.counterId : !!resolveCreatePayload().counterId,
);

useTemporaryLocale(() =>
  hasActiveCounter.value || !booking.value ? undefined : getBookingProcessLanguage(booking.value),
);

watchEffect(() => {
  if (booking.value) {
    const { customerData, allocations } = booking.value;
    const vehicleType = allocations[0].car.vehicleType;
    uiStore.setHeaderTitle(
      t('handover'),
      `${customerData?.firstName ?? 'n/a'} ${customerData?.lastName ?? 'n/a'}`,
      `${vehicleType.make.name} ${vehicleType.model}`,
    );
  } else {
    uiStore.setHeaderTitle(t('handover'));
  }
});

const onCancelClicked = async () => {
  const result = await Alert.fire<string, 'resetAndLeave' | 'justLeave' | 'stay'>({
    title: t('cancelHandover'),
    text: t('cancelHandoverConfirmation'),
    icon: 'warning',
    buttons: [
      { propertyName: 'resetAndLeave', text: t('resetAndLeave'), style: 'danger' },
      { propertyName: 'justLeave', text: t('justLeave'), style: 'secondary' },
      { propertyName: 'stay', text: t('stay'), style: 'ghost' },
    ],
  });

  if (result.resetAndLeave) {
    await clearProcessSession();
    await navigateToList();
  } else if (result.justLeave) {
    await navigateToList({ preserveSession: true });
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "handover": "Handover",
    "back": "Back",
    "cancelHandover": "Cancel Handover",
    "cancelHandoverConfirmation": "Are you sure you want to cancel the handover?",
    "resetAndLeave": "Yes, reset progress",
    "justLeave": "Just go back",
    "stay": "Stay here"
  },
  "de": {
    "handover": "Übergabe",
    "back": "Zurück",
    "cancelHandover": "Übergabe abbrechen",
    "cancelHandoverConfirmation": "Möchten Sie die Übergabe wirklich abbrechen?",
    "resetAndLeave": "Ja, Fortschritt zurücksetzen",
    "justLeave": "Nur zurück",
    "stay": "Hier bleiben"
  },
  "es": {
    "handover": "Entrega",
    "back": "Volver",
    "cancelHandover": "Cancelar entrega",
    "cancelHandoverConfirmation": "¿Seguro que desea cancelar la entrega?",
    "resetAndLeave": "Sí, restablecer el progreso",
    "justLeave": "Solo volver atrás",
    "stay": "Permanecer aquí"
  }
}
</i18n>
